import type { JSX } from '@builder.io/qwik';
import { useContext } from '@builder.io/qwik';
import { component$ } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import type { MainCategoryInterface } from '~/types/AppData/MainCategoryInterface';
import type { VerticalAlignment } from '~/types/AppData/PageBuilder/CardBlockInterface';
import type { AuthorInterface } from '~/types/AppData/PageContent/AuthorInterface';
import type { TYPOGRAPHY_SIZE } from '~/types/AppData/Theme/TypographyInterface';
import {
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';

interface CardTextInterface {
  title: string;
  description?: string;
  showDescription?: boolean;
  authors?: AuthorInterface[];
  showAuthor?: boolean;
  date?: string;
  showDate?: boolean;
  showImage?: boolean;
  showTitle?: boolean;
  wrapperClass?: string;
  showMainCategory?: boolean;
  mainCategory?: MainCategoryInterface | null;
  datePosition?: VerticalAlignment;
  isSingle?: boolean;
  hoverColor?: 'primary' | 'secondary';
  titleSize?: TYPOGRAPHY_SIZE;
  descriptionSize?: TYPOGRAPHY_SIZE;
  cardLink?: string;
}

const titleClasses = cva(
  // Line height is set to 1.3em to work for SI and Swimsuit styles
  'line-clamp-3 overflow-hidden text-ellipsis antialiased leading-[1.3em]',
  {
    variants: {
      showOnlyTitle: {
        true: 'font-group-tiny leading-4',
      },
      isSingle: {
        true: '',
      },
      hoverColor: {
        primary: 'group-hover/card:text-primary',
        secondary: 'group-hover/card:text-secondary',
      },
      sizeSm: getCSSSizeSmText(),
      sizeMd: getCSSSizeMdText(),
      sizeLg: getCSSSizeLgText(),
    },
    compoundVariants: [
      {
        showOnlyTitle: false,
        isSingle: true,
        class: 'font-group-huge',
      },
      {
        showOnlyTitle: false,
        isSingle: false,
        class: 'font-group-bigger',
      },
    ],
  }
);

const descriptionClasses = cva(
  'line-clamp-3 overflow-hidden text-ellipsis antialiased',
  {
    variants: {
      sizeSm: getCSSSizeSmText(),
      sizeMd: getCSSSizeMdText(),
      sizeLg: getCSSSizeLgText(),
    },
  }
);

export default component$<CardTextInterface>(
  ({
    title,
    description,
    showDescription,
    showTitle = true,
    authors,
    showAuthor,
    date,
    showDate,
    showImage,
    wrapperClass,
    mainCategory,
    showMainCategory,
    datePosition,
    isSingle,
    titleSize,
    descriptionSize = 'big',
    cardLink,
  }): JSX.Element => {
    const { themeComponents } = useContext(AppDataContext);
    const showDateTop = showDate && datePosition === 'top';
    const showDateBottom = showDate && datePosition === 'bottom';
    const showOnlyTitle =
      !showImage && !showDescription && !showAuthor && !showDate;

    const hasAuthors =
      !!authors && authors.filter((author) => author.name).length > 0;
    const hasDate = !!date && date.length > 0;

    return (
      <div class={wrapperClass}>
        {showMainCategory && mainCategory?.displayName && (
          <span class='block font-group-tiny relative'>
            {mainCategory.displayName}
            <span class='absolute left-0 bottom-0 w-full h-[3px] bg-primary'></span>
          </span>
        )}

        {showDateTop && (
          <span class='block font-group-tiny relative mb-1'>
            <time>{date}</time>
            <span class='absolute left-0 bottom-0 w-full h-[3px] bg-[#00A3DA]'></span>
          </span>
        )}

        <div class='flex flex-col gap-1'>
          {showTitle && (
            <a
              href={cardLink}
              class='pointer-events-none'
              aria-hidden='true'
              tabIndex={-1}
            >
              <h3
                id={title}
                class={titleClasses({
                  showOnlyTitle,

                  /*
                   * Using this part of the theme to determine the hover color to have consistency
                   * between homepage galleries and related article sections on SI.com and OnSI
                   */
                  hoverColor:
                    themeComponents.articleBlock.paragraphLinkColor.value ===
                    'secondary'
                      ? 'secondary'
                      : 'primary',
                  isSingle: isSingle ?? true,
                  ...(titleSize && {
                    sizeSm: titleSize,
                    sizeMd: titleSize,
                    sizeLg: titleSize,
                  }),
                })}
              >
                {title}
              </h3>
            </a>
          )}
          {showDescription && description && (
            <span
              class={descriptionClasses({
                sizeSm: descriptionSize,
                sizeMd: descriptionSize,
                sizeLg: descriptionSize,
              })}
            >
              {description}
            </span>
          )}
        </div>

        {(showAuthor || showDateBottom) && (
          <footer class='card-footer font-group-tiny'>
            <span>
              {showAuthor && (
                <>
                  {authors?.map((each, index) => {
                    return (
                      <>
                        {each.pagePath ? (
                          <a
                            class='relative z-10'
                            href={each.pagePath}
                            rel='author'
                            data-testtype='author-link'
                            title={each.name}
                          >
                            {each.name}
                          </a>
                        ) : (
                          <span key={each.name}>{each.name}</span>
                        )}
                        {index < authors.length - 1 && ', '}
                      </>
                    );
                  })}
                </>
              )}
              {showAuthor && showDateBottom && hasAuthors && hasDate && (
                <span class='mx-1'>|</span>
              )}
              {showDateBottom && <time>{date}</time>}
            </span>
          </footer>
        )}
      </div>
    );
  }
);
